import { GOOGLE_FONT_STYLESHEETS, LOCAL_FONT_FAMILIES } from './fontPreview.consts';

const DIALOG_ID = 'font-preview-dialog';

const BASELINE_FONTS: ReadonlyArray<string> = ['monospace', 'serif', 'sans-serif'];
const SAMPLE_GLYPHS = 'mmmmmwwwwwlliiOO';

export const isFontLoaded = (family: string): boolean => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        return false;
    }

    return BASELINE_FONTS.some((baseline) => {
        context.font = `72px ${baseline}`;
        const baselineWidth = context.measureText(SAMPLE_GLYPHS).width;
        context.font = `72px "${family}", ${baseline}`;
        const familyWidth = context.measureText(SAMPLE_GLYPHS).width;
        return familyWidth !== baselineWidth;
    });
};

export const injectGoogleFontPreviewStylesheets = (): void => {
    Object.values(GOOGLE_FONT_STYLESHEETS).forEach((href) => {
        if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
            return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
    });
};

export const getFontPreviewFamilies = (): ReadonlyArray<string> =>
    Object.keys(GOOGLE_FONT_STYLESHEETS).concat(LOCAL_FONT_FAMILIES);

const createRow = (family: string): HTMLDivElement => {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid #eee';

    const indicator = document.createElement('span');
    indicator.style.cssText = 'width:20px';
    indicator.textContent = isFontLoaded(family) ? '\u2705' : '\u274C';

    const name = document.createElement('span');
    name.style.cssText = 'min-width:170px;font-size:12px;color:#333';
    name.textContent = family;

    const sample = document.createElement('span');
    sample.style.cssText = `font-size:20px;color:#000;font-family:"${family}"`;
    sample.textContent = `Aa Gg 123 \u2014 ${family}`;

    row.appendChild(indicator);
    row.appendChild(name);
    row.appendChild(sample);
    return row;
};

/**
 * Toggle a floating dialog that lists every known font family with a loaded
 * indicator (canvas width comparison) and a live glyph sample.
 *
 * @example
 * // In the browser console on a running app:
 * showFontPreview();
 */
export const showFontPreview = (): void => {
    const existing = document.getElementById(DIALOG_ID);
    if (existing) {
        existing.remove();
        return;
    }

    injectGoogleFontPreviewStylesheets();

    const allFamilies = getFontPreviewFamilies();

    const dialog = document.createElement('div');
    dialog.id = DIALOG_ID;
    dialog.style.cssText = [
        'position:fixed',
        'top:24px',
        'right:24px',
        'z-index:2147483647',
        'width:460px',
        'max-height:80vh',
        'display:flex',
        'flex-direction:column',
        'background:#ffffff',
        'color:#111111',
        'border:1px solid #ccc',
        'border-radius:8px',
        'box-shadow:0 8px 32px rgba(0,0,0,0.25)',
        'font-family:monospace',
        'overflow:hidden',
    ].join(';');

    const header = document.createElement('div');
    header.style.cssText =
        'display:flex;align-items:center;justify-content:space-between;padding:10px 12px;background:#111;color:#fff';

    const title = document.createElement('strong');
    title.textContent = 'Font Renderer';

    const closeButton = document.createElement('button');
    closeButton.textContent = 'Close';
    closeButton.style.cssText = 'cursor:pointer;padding:4px 10px;border-radius:4px;border:none';
    closeButton.onclick = (): void => dialog.remove();

    header.appendChild(title);
    header.appendChild(closeButton);

    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;padding:8px';

    const renderRows = (): void => {
        list.innerHTML = '';
        allFamilies.forEach((family) => list.appendChild(createRow(family)));
    };

    dialog.appendChild(header);
    dialog.appendChild(list);
    document.body.appendChild(dialog);

    renderRows();
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
            renderRows();
            window.setTimeout(renderRows, 1500);
        });
    }
};
